import { computed, ComputedRef, Ref, ref } from "vue";
import { defineStore } from "pinia";
import { useCustomerStore } from "../../../base/ts/stores/CustomerStore";
import { collect } from "collect.js";
import ApiService from "../../../base/ts/composables/ApiService";
import { type WoodvisionAvailabilityAPIResponse } from "../types/WoodvisionAvailabilityAPIResponse";
import { NextStepStates, useCheckoutStore } from "../../../base/ts/stores/CheckoutStore";
import DayJs from "../../../base/ts/composables/DayJs";

export const useWoodvisionAvailabilityStore = defineStore("woodvisionAvailability", () => {
    // Data
    const postalCodeData: Ref<{ postal_code: string; country_code: string }> = ref(getSavedPostalCodeData());

    const basketAvailability: Ref<{
        orderLevelDeliveryDates: string[] | null;
        orderLevelAvailability: boolean;
        entries: {
            [key: number]: WoodvisionAvailabilityAPIResponse["items"][0] & {
                product_parts?: {
                    [key: number]: WoodvisionAvailabilityAPIResponse["items"][0];
                };
            };
        };
    }> = ref(null);
    const isFetchingBasketAvailability: Ref<boolean> = ref(false);
    let apiService: ApiService = new ApiService();

    // Pinia stores
    const customerStore = useCustomerStore();
    const checkoutStore = useCheckoutStore();

    // Methods
    function setPostalCodePayload(postalCode: string, countryCode: string) {
        postalCodeData.value = { postal_code: postalCode.trim(), country_code: countryCode };
    }

    function setPostalCodeCookie(postalCode: string, countryCode: string) {
        localStorage.setItem("postcode_check.postal_code", postalCode);
        localStorage.setItem("postcode_check.country_code", countryCode);
    }

    function resetPostalCodePayload() {
        postalCodeData.value.postal_code = null;
    }

    function getProductsPayload(availabilitySkus: { sku: string; amount: number }[]): {
        skus: { sku: string; amount: number }[];
        postal_code?: string;
        country_code?: string;
    } {
        let postalCode =
            postalCodeData.value.postal_code ?? customerStore?.customer?.primaryDeliveryAddress?.postalcode ?? null;
        let countryCode =
            postalCodeData.value.country_code ?? customerStore?.customer?.primaryDeliveryAddress?.country_code ?? null;

        let payload: {
            skus: { sku: string; amount: number }[];
            postal_code?: string;
            country_code?: string;
        } = { skus: availabilitySkus };

        if (postalCode && countryCode) {
            payload.postal_code = postalCode;
            payload.country_code = countryCode;
        }

        return payload;
    }

    function getBasketPayload(): { postal_code?: string; country_code?: string } {
        let postalCode =
            postalCodeData.value.postal_code ?? customerStore?.customer?.primaryDeliveryAddress?.postalcode ?? null;
        let countryCode =
            postalCodeData.value.country_code ?? customerStore?.customer?.primaryDeliveryAddress?.country_code ?? null;

        let payload: {
            postal_code?: string;
            country_code?: string;
        } = {};

        if (postalCode && countryCode) {
            payload.postal_code = postalCode;
            payload.country_code = countryCode;
        }

        return payload;
    }

    function getWoodvisionProductsAvailabilityFirstDate(
        availabilitySkus: { sku: string; amount: number }[],
    ): Promise<string | null> {
        return getWoodvisionProductsAvailability(availabilitySkus)
            .then((response) => {
                if (!response) {
                    return null;
                }

                let deliveryDate: string = collect(response?.orderLevelDeliveryDates ?? [])
                    .map((deliveryDate: string) => {
                        return DayJs.getInstance()(new Date(deliveryDate)).format("YYYY-MM-DD");
                    })
                    .sort()
                    .first();

                return deliveryDate ?? null;
            })
            .catch((exception) => {
                return Promise.reject(exception);
            });
    }

    function getWoodvisionProductsAvailability(
        availabilitySkus: { sku: string; amount: number }[],
    ): Promise<WoodvisionAvailabilityAPIResponse | null> {
        let payload = getProductsPayload(availabilitySkus);

        return apiService
            .post("woodvision-availability-api/products", payload)
            .then((response) => {
                setPostalCodeCookie(payload.postal_code, payload.country_code);

                return Object.keys(response?.data).length > 0 ? response?.data : null;
            })
            .catch((exception) => {
                resetPostalCodePayload();

                return Promise.reject(exception);
            });
    }

    async function getWoodvisionBasketAvailability(ignoreCookiePostalCode: boolean = false): Promise<void> {
        isFetchingBasketAvailability.value = true;
        let payload = ignoreCookiePostalCode ? {} : getBasketPayload();

        // Fetch the Basket availability
        return apiService
            .post("woodvision-availability-api/basket", payload)
            .then((response) => {
                if (!ignoreCookiePostalCode) {
                    setPostalCodeCookie(payload.postal_code, payload.country_code);
                }

                basketAvailability.value = Object.keys(response?.data).length > 0 ? response?.data : null;
            })
            .catch((exception) => {
                basketAvailability.value = null;
                resetPostalCodePayload();

                return Promise.reject(exception);
            })
            .finally(() => {
                isFetchingBasketAvailability.value = false;
                checkoutStore.customOverwriteNextStepStateToDisabled =
                    !!basketAvailability.value && !basketAvailability.value.orderLevelAvailability;
            });
    }

    function getSavedPostalCodeData() {
        let postalCode = localStorage.getItem("postcode_check.postal_code");
        let countryCode = localStorage.getItem("postcode_check.country_code");

        if (!postalCode || postalCode === "undefined" || !countryCode || countryCode === "undefined") {
            return { postal_code: null, country_code: null };
        }

        return { postal_code: postalCode, country_code: countryCode };
    }

    return {
        postalCodeData,
        basketAvailability,
        isFetchingBasketAvailability,
        setPostalCodeCookie,
        setPostalCodePayload,
        getWoodvisionProductsAvailabilityFirstDate,
        getWoodvisionBasketAvailability,
    };
});
